import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { listCompanyPackages } from "~/services/medicalcompany/advanceRequest";
import { useAuth } from "~/auth/AuthProvider";
import CompanyPackageReview from "~/components/companyReview/CompanyPackageReview";
import { getApiErrorMessage, isCanceledRequest } from "~/utils/apiError";
import { formatIsoDateBR, plural } from "~/utils/format";
import { formatBRL, formatDateBR } from "~/shared/ui/format";
import PageHeader from "~/shared/ui/PageHeader";
import EmptyState from "~/shared/ui/EmptyState";
import ErrorState from "~/shared/ui/ErrorState";
import InactiveChip from "~/shared/ui/InactiveChip";
import StatusChip from "~/shared/ui/StatusChip";
import { isExpiredRow } from "~/features/packages";
import { useFeatures } from "~/hooks/useFeatures";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import {
  COMPANY_PAGE_SIZE,
  companyFiltersToApi,
  companyFiltersToSearch,
  DEFAULT_COMPANY_FILTERS,
  keepAllowedCompanies,
  parseCompanyFilters,
} from "~/utils/packageFilters";
import CompanyFilterBar from "./CompanyFilterBar";

const COLS = 10;
// Rótulo só para leitor de tela. Medidas em string: no `sx` do MUI, números entre 0 e 1 viram porcentagem (width: 1 = 100%).
const SR_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  margin: "-1px",
  padding: 0,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};
const PAGE_ARIA = { first: "Ir para a primeira página", last: "Ir para a última página", next: "Ir para a próxima página", previous: "Ir para a página anterior" };

const activeFilterLabels = (f, companies) => {
  const labels = [];
  if (f.id) labels.push(`Pacote: #${f.id}`);
  if (f.companyIds.length) labels.push(`Empresas: ${f.companyIds.map((id) => companies.find((c) => String(c.id) === id)?.name || `#${id}`).join(", ")}`);
  if (f.doctorName) labels.push(`Médico: ${f.doctorName}`);
  if (f.crm) labels.push(`CRM: ${f.crm}${f.crmUf ? `/${f.crmUf}` : ""}`);
  if (f.pendingOnly) labels.push("Pendentes de conferência");
  if (f.shiftFrom || f.shiftTo) labels.push(`Plantões de ${formatIsoDateBR(f.shiftFrom) || "…"} a ${formatIsoDateBR(f.shiftTo) || "…"}`);
  if (f.requestFrom || f.requestTo) labels.push(`Pedidos de ${formatIsoDateBR(f.requestFrom) || "…"} a ${formatIsoDateBR(f.requestTo) || "…"}`);
  return labels;
};

/** Aviso de envio (mockup T2). Números vêm do `complete` (AB D18); sem eles, texto sem número (D3). */
export const sentMessage = ({ packageId, splitCount, splitChildId, autoMergedIntoId, refusedAll }) => {
  // B1a: recusa total encerra o pacote, sem divisão.
  if (refusedAll) return `Pacote #${packageId} recusado: nenhum plantão será antecipado e o médico verá o pedido como Recusado. Seus filtros continuam.`;
  let text = `Pacote #${packageId} enviado.`;
  if (splitCount && splitChildId) {
    text += splitCount === 1 ? ` O plantão não antecipado está no novo pacote #${splitChildId}.` : ` Os ${splitCount} plantões não antecipados estão no novo pacote #${splitChildId}.`;
  } else if (splitCount) {
    text += ` ${plural(splitCount, "plantão não antecipado foi", "plantões não antecipados foram")} para um novo pacote, que aparece na lista.`;
  }
  if (autoMergedIntoId) text += ` O pacote enviado foi juntado ao #${autoMergedIntoId} (mesmo médico e empresa).`;
  return `${text} Seus filtros continuam.`;
};

export default function TableMedicalCompany() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { me } = useAuth();
  const notify = useNotify();
  const { features } = useFeatures();
  const companies = useMemo(() => me?.companies || [], [me]);
  const rawFilters = useMemo(() => parseCompanyFilters(searchParams), [searchParams]);
  const filters = useMemo(() => keepAllowedCompanies(rawFilters, companies), [rawFilters, companies]);
  const apiKey = JSON.stringify(companyFiltersToApi(filters));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const rowRefs = useRef({});
  const containerRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(0);

  useEffect(() => {
    if (filters !== rawFilters) setSearchParams(companyFiltersToSearch(filters), { replace: true });
  }, [filters, rawFilters, setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    // Loading/erro/linhas mudam no mesmo lote: o spinner nunca fica por cima da mensagem de erro (E4).
    listCompanyPackages(JSON.parse(apiKey), { signal: controller.signal }).then(
      (result) => {
        if (controller.signal.aborted) return;
        setRows(result.rows);
        setTotal(result.total);
        setLoading(false);
      },
      (e) => {
        if (controller.signal.aborted || isCanceledRequest(e)) return;
        setError(getApiErrorMessage(e, "Não foi possível carregar as solicitações."));
        setLoading(false);
      }
    );
    return () => controller.abort();
  }, [apiKey, reloadToken]);

  // O painel aberto fica com a largura visível da tabela (não rola junto na horizontal).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => setPanelWidth(el.clientWidth));
    observer.observe(el);
    setPanelWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const update = (patch) => setSearchParams(companyFiltersToSearch({ ...filters, ...patch }));

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(total / COMPANY_PAGE_SIZE));
    if (!loading && !error && total > 0 && filters.page > lastPage) update({ page: lastPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, total, filters.page]);

  const openRow = rows.find((r) => String(r.id) === filters.packageId) || null;
  const scrolledFor = useRef("");
  useEffect(() => {
    if (!openRow || scrolledFor.current === filters.packageId) return;
    scrolledFor.current = filters.packageId;
    rowRefs.current[openRow.id]?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [openRow, filters.packageId]);

  const closePanel = () => {
    // Recarrega com os mesmos filtros/página: contadores e "Pendentes de conferência" refletem o que foi conferido.
    scrolledFor.current = "";
    update({ packageId: "" });
    setReloadToken((t) => t + 1);
    notify({ severity: "info", message: "Pacote fechado. Seus filtros continuam." });
  };
  const toggle = (id) => (String(id) === filters.packageId ? closePanel() : update({ packageId: String(id) }));

  const panel = (row) => (
    <CompanyPackageReview
      key={filters.packageId}
      packageId={Number(filters.packageId)}
      packageRow={row}
      onClose={closePanel}
      onSent={(sent) => {
        update({ packageId: "" });
        setReloadToken((t) => t + 1);
        notify({ severity: "success", message: sentMessage(sent) });
      }}
      onOpenPackage={(id) => {
        // Veio do aviso "agrupado no #X": a origem saiu da lista, então recarrega com os mesmos filtros.
        update({ packageId: String(id) });
        setReloadToken((t) => t + 1);
      }}
    />
  );

  const labels = activeFilterLabels(filters, companies);
  // Pacote da URL fora da página atual (ex.: destino de agrupamento): abre num quadro acima da tabela.
  const orphanOpen = Boolean(filters.packageId) && !loading && !openRow;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <PageHeader title="Conferência de antecipações" description="Pacotes das empresas que você gerencia" />
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <CompanyFilterBar
          value={filters}
          companies={companies}
          onApply={(draft) => setSearchParams(companyFiltersToSearch({ ...draft, order: filters.order, page: 1, packageId: filters.packageId }))}
          onClear={() => setSearchParams(companyFiltersToSearch({ ...DEFAULT_COMPANY_FILTERS, packageId: filters.packageId }))}
        />
      </Paper>
      {labels.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 1, fontSize: 13, color: "text.secondary" }}>
          <span>Filtros ativos:</span>
          {labels.map((label) => <Chip key={label} size="small" color="primary" label={label} />)}
          <span>· continuam ao trocar de página, ao fechar um pacote e depois de enviar</span>
        </Box>
      )}
      {orphanOpen && <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 1.5, borderTop: "2px solid #05bbe5" }}>{panel(null)}</Paper>}
      {error ? (
        <Paper variant="outlined"><ErrorState description={error} onRetry={() => setReloadToken((t) => t + 1)} /></Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          {loading && rows.length > 0 && <LinearProgress />}
          <TableContainer ref={containerRef}>
            <Table size="small" sx={{ minWidth: 1000 }} aria-label="Pacotes para conferir">
              <TableHead>
                <TableRow>
                  <TableCell>Pacote</TableCell>
                  <TableCell>Médico</TableCell>
                  <TableCell>CRM</TableCell>
                  <TableCell>Empresa</TableCell>
                  <TableCell sortDirection={filters.order}>
                    <TableSortLabel active direction={filters.order} onClick={() => update({ order: filters.order === "desc" ? "asc" : "desc", page: 1 })}>Pedido em</TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Plantões</TableCell>
                  <TableCell>Conferidos</TableCell>
                  <TableCell align="right">Valor bruto</TableCell>
                  <TableCell>Aviso</TableCell>
                  <TableCell align="right" sx={{ position: "relative" }}>
                    <Box component="span" sx={SR_ONLY}>Ações</Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && rows.length === 0 &&
                  [0, 1, 2].map((i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: COLS }).map((_, j) => <TableCell key={j}><Skeleton width={j === 1 ? 160 : 60} /></TableCell>)}
                    </TableRow>
                  ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLS} sx={{ border: 0 }}>
                      <EmptyState
                        title="Nenhum pacote com esses filtros"
                        description={
                          companies.some((c) => c.active === false)
                            ? "Revise os filtros ou o período dos pedidos. Pacotes de empresas desativadas só aparecem com “Incluir empresas inativas”."
                            : "Revise os filtros ou o período dos pedidos."
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => {
                  const open = String(row.id) === filters.packageId;
                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        hover
                        selected={open}
                        data-testid={`package-row-${row.id}`}
                        ref={(el) => { rowRefs.current[row.id] = el; }}
                        sx={{ "& > td": { borderBottom: open ? "none" : undefined } }}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <b>#{row.id}</b>
                          {/* F1b: só quando o BE devolver expirados nesta lista (lacuna 9); sem isso, o 409 avisa ao agir */}
                          {features.packageExpiration && isExpiredRow(row) && (
                            <Box component="span" sx={{ ml: 0.75 }}><StatusChip status={row.status} /></Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{row.doctor_name}</TableCell>
                        <TableCell>{`${row.doctor_crm}/${row.doctor_crm_uf}`}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {row.company_name}
                          {/* BE devolve company_active na doctorrequets; ausente (BE antigo) = ativa */}
                          {row.company_active === false && <InactiveChip sx={{ ml: 0.75 }} />}
                        </TableCell>
                        <TableCell>{formatDateBR(row.request_date)}</TableCell>
                        <TableCell align="right">{row.vacancies_qty}</TableCell>
                        <TableCell>{`${row.checked_vacancies} de ${row.vacancies_qty}`}</TableCell>
                        <TableCell align="right">{formatBRL(row.total_gross_value)}</TableCell>
                        <TableCell>
                          {row.has_non_validated_vacancies ? (
                            <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 500, whiteSpace: "nowrap" }}>
                              {`⚠ ${plural(row.non_validated_vacancies_qty, "valor não validado", "valores não validados")}`}
                            </Typography>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button size="small" variant={open ? "contained" : "outlined"} aria-expanded={open} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />} onClick={() => toggle(row.id)} sx={{ whiteSpace: "nowrap" }}>
                            {open ? "Fechar" : "Ver plantões"}
                          </Button>
                        </TableCell>
                      </TableRow>
                      <TableRow data-testid={`package-detail-${row.id}`}>
                        <TableCell colSpan={COLS} sx={{ p: 0, borderBottom: open ? undefined : "none" }}>
                          <Collapse in={open} timeout={220} unmountOnExit>
                            <Box sx={{ position: "sticky", left: 0, width: panelWidth || "100%", p: { xs: 1.5, md: 2 }, bgcolor: "#f3f8fa", borderTop: "2px solid #05bbe5" }}>
                              {open && panel(row)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={Math.max(0, filters.page - 1)}
            rowsPerPage={COMPANY_PAGE_SIZE}
            rowsPerPageOptions={[COMPANY_PAGE_SIZE]}
            onPageChange={(_, next) => update({ page: next + 1 })}
            labelRowsPerPage="Linhas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            getItemAriaLabel={(type) => PAGE_ARIA[type]}
          />
        </Paper>
      )}
    </Box>
  );
}
